import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Statistic,
  DatePicker,
  Button,
  Space,
  Tag,
  Dropdown,
  Modal,
  Form,
  Input,
  Select,
  Alert,
  Descriptions,
  Typography,
  Spin,
  Badge,
  Avatar,
  Tooltip,
  message,
} from "antd";
import {
  DollarOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  MoreOutlined,
  CheckOutlined,
  EyeOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import PageMeta from "~/components/common/meta";
import { BillingSubNav } from "~/components/admin/billing";
import {
  useGetPayrollInvoices,
  useGetPayrollDashboard,
  useMarkPayrollInvoiceAsPaid,
  useCreatePayrollInvoiceWithQuickBooks,
  useGetPayrollInvoice,
  payrollInvoiceService,
} from "~/services/payroll";
import { useGetAllStaff, operationServices } from "~/services/operation";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OPERATION_KEYS } from "~/services/operation/keys";
import { usePayrollSettings } from "~/hooks/usePayrollSettings";
import { useNotification } from "~/hooks";
import { formatStaffName } from "~/utils/format-staff-name";
import {
  PayrollInvoiceStatus,
  PayrollInvoice,
  PayrollPeriodType,
} from "~/types/payroll";
// QuickBooks connection check removed — Stripe handles payments
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const BREAK_OPTIONS = [0, 15, 30, 45, 60];

export const ScreenPayroll: React.FC = () => {
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [clockOutModalVisible, setClockOutModalVisible] = useState(false);
  const [clockOutStaff, setClockOutStaff] = useState<any>(null);
  const [clockOutForm] = Form.useForm();
  const [editSessionModal, setEditSessionModal] = useState<any>(null);
  const [editSessionForm] = Form.useForm();
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [wsPage, setWsPage] = useState(1);
  const [wsStaffFilter, setWsStaffFilter] = useState<string | undefined>();
  const [wsDateRange, setWsDateRange] = useState<[string, string] | null>(null);
  const [form] = Form.useForm();
  const [qbConnectionStatus, setQbConnectionStatus] =
    useState<any>({ isConnected: true });

  const { success } = useNotification();

  const { data: payrollInvoices, isLoading: invoicesLoading } =
    useGetPayrollInvoices({
      page: 1,
      limit: 50,
      startDate: dateRange?.[0],
      endDate: dateRange?.[1],
    });

  const { data: dashboardData, isLoading: dashboardLoading } =
    useGetPayrollDashboard(dateRange?.[0], dateRange?.[1]);

  const { data: staffsData, isLoading: staffsLoading } = useGetAllStaff({
    getAll: true,
  });
  const { settings: payrollSettings, isLoading: settingsLoading } =
    usePayrollSettings();

  const queryClient = useQueryClient();
  const markAsPaidMutation = useMarkPayrollInvoiceAsPaid();
  const createWithQuickBooksMutation = useCreatePayrollInvoiceWithQuickBooks();

  const { data: workSessionsData, isLoading: wsLoading } = useQuery({
    queryKey: ["workSessions", wsPage, wsStaffFilter, wsDateRange],
    queryFn: () =>
      payrollInvoiceService.getWorkSessions({
        page: wsPage,
        limit: 20,
        staffId: wsStaffFilter,
        startDate: wsDateRange?.[0],
        endDate: wsDateRange?.[1],
      }),
  });

  const editSessionMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      payrollInvoiceService.updateWorkSession(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workSessions"] });
      success({ message: "Work session updated" });
      setEditSessionModal(null);
    },
    onError: () => {
      message.error("Failed to update work session");
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: (id: string) => payrollInvoiceService.deleteWorkSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workSessions"] });
      queryClient.invalidateQueries({ queryKey: [OPERATION_KEYS.STAFF] });
      success({ message: "Work session deleted" });
    },
    onError: () => {
      message.error("Failed to delete work session");
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: ({ staffId, body }: { staffId: string; body?: { notes?: string; unpaidBreakMinutes?: number } }) =>
      operationServices.clockOut(staffId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OPERATION_KEYS.STAFF] });
      queryClient.invalidateQueries({ queryKey: ["workSessions"] });
      success({ message: "Staff clocked out successfully" });
      setClockOutModalVisible(false);
      setClockOutStaff(null);
      clockOutForm.resetFields();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message || "Failed to clock out staff member";
      // If the error indicates no active session, the backend auto-resets the flag
      if (msg.includes("reset")) {
        message.warning(msg + " Refreshing...");
        queryClient.invalidateQueries({ queryKey: [OPERATION_KEYS.STAFF] });
        setClockOutModalVisible(false);
        setClockOutStaff(null);
      } else {
        message.error(msg);
      }
    },
  });

  const { data: invoiceDetail, isLoading: detailLoading } = useQuery({
    queryKey: ["payrollInvoiceDetail", selectedInvoiceId],
    queryFn: () => payrollInvoiceService.getDetail(selectedInvoiceId!),
    enabled: !!selectedInvoiceId,
  });

  const clockedInStaff = useMemo(() => {
    if (!staffsData?.items) return [];
    return staffsData.items.filter((s: any) => s.isClockedIn);
  }, [staffsData]);

  useEffect(() => {
    if (payrollSettings && !form.getFieldValue("period")) {
      const today = dayjs();
      let startDate, endDate;

      switch (payrollSettings.payPeriod) {
        case "weekly":
          startDate = today.startOf("week");
          endDate = today.endOf("week");
          break;
        case "biweekly":
          startDate = today.startOf("week");
          endDate = today.add(1, "week").endOf("week");
          break;
        case "monthly":
          startDate = today.startOf("month");
          endDate = today.endOf("month");
          break;
        default:
          startDate = today.startOf("week");
          endDate = today.endOf("week");
      }

      form.setFieldsValue({
        period: [startDate, endDate],
        periodType:
          payrollSettings.payPeriod === "biweekly"
            ? PayrollPeriodType.BIWEEKLY
            : payrollSettings.payPeriod === "monthly"
            ? PayrollPeriodType.MONTHLY
            : PayrollPeriodType.WEEKLY,
      });
    }
  }, [payrollSettings, form]);

  useEffect(() => {
    checkQuickBooksConnection();
  }, []);

  // Stripe connection is always available — no OAuth needed
  const checkQuickBooksConnection = () => {
    setQbConnectionStatus({ isConnected: true });
  };

  const handleConnectQuickBooks = () => {
    // No-op — Stripe is configured via env vars
  };

  const getStatusColor = (status: PayrollInvoiceStatus) => {
    switch (status) {
      case PayrollInvoiceStatus.DRAFT:
        return "default";
      case PayrollInvoiceStatus.PENDING:
        return "processing";
      case PayrollInvoiceStatus.APPROVED:
        return "warning";
      case PayrollInvoiceStatus.PAID:
        return "success";
      case PayrollInvoiceStatus.CANCELLED:
        return "error";
      default:
        return "default";
    }
  };

  const handleMarkAsPaid = (id: string) => {
    markAsPaidMutation.mutate(id);
  };

  const handleCreatePayrollInvoice = () => {
    setCreateModalVisible(true);
  };

  const handleCreateSubmit = async (values: any) => {
    try {
      const result = await createWithQuickBooksMutation.mutateAsync({
        staffId: values.staffId,
        periodStart: values.period[0].format("YYYY-MM-DD"),
        periodEnd: values.period[1].format("YYYY-MM-DD"),
        periodType: values.periodType,
        notes: values.notes,
        autoCalculate: true,
      });

      setCreateModalVisible(false);
      form.resetFields();

      const staffName = staffsData?.items?.find(
        (staff: any) => staff.id === values.staffId
      );
      const staffFullName = staffName
        ? `${staffName.user?.firstName} ${staffName.user?.lastName}`
        : "Employee";

      success({
        message: "✅ Payroll Invoice Created Successfully!",
        description: (
          <div>
            <div>
              Employee: <span className="font-medium">{staffFullName}</span>
            </div>
            <div>
              Period: {values.period[0].format("MMM DD")} -{" "}
              {values.period[1].format("MMM DD, YYYY")}
            </div>
            <div>
              QuickBooks: <span className="text-green-600">✓ Synced</span>
            </div>
            {(result as any)?.data?.invoiceNumber && (
              <div>
                Invoice #:{" "}
                <span className="font-medium">
                  {(result as any).data.invoiceNumber}
                </span>
              </div>
            )}
          </div>
        ),
      });
    } catch (error: any) {
      console.error("Error creating payroll invoice:", error);
      error({
        message: "❌ Failed to Create Payroll Invoice",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred",
      });
    }
  };

  const handleCreateCancel = () => {
    setCreateModalVisible(false);
    form.resetFields();
  };

  const handleViewDetail = (invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
    setDetailModalVisible(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setSelectedInvoiceId(null);
  };

  const handleOpenClockOut = (staff: any) => {
    setClockOutStaff(staff);
    clockOutForm.resetFields();
    setClockOutModalVisible(true);
  };

  const handleClockOutSubmit = (values: any) => {
    if (!clockOutStaff) return;
    clockOutMutation.mutate({
      staffId: clockOutStaff.id,
      body: {
        notes: values.notes?.trim() || undefined,
        unpaidBreakMinutes: values.unpaidBreakMinutes ?? 0,
      },
    });
  };

  const columns: ColumnsType<PayrollInvoice> = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
    },
    {
      title: "Employee",
      key: "employee",
      render: (_, record) => (
        <div>
          <div className="font-medium">
            {record.staff?.user?.firstName} {record.staff?.user?.lastName}
          </div>
          <div className="text-gray-500 text-sm">
            {record.staff?.user?.email}
          </div>
        </div>
      ),
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <div className="text-sm">
          {new Date(record.periodStart).toLocaleDateString()} -{" "}
          {new Date(record.periodEnd).toLocaleDateString()}
        </div>
      ),
    },
    {
      title: "Regular Hours",
      dataIndex: "regularHours",
      key: "regularHours",
      render: (hours: any) => `${(Number(hours) || 0).toFixed(1)}h`,
    },
    {
      title: "Overtime Hours",
      dataIndex: "overtimeHours",
      key: "overtimeHours",
      render: (hours: any) => `${(Number(hours) || 0).toFixed(1)}h`,
    },
    {
      title: "Gross Pay",
      dataIndex: "grossPay",
      key: "grossPay",
      render: (amount: any) => `$${(Number(amount) || 0).toFixed(2)}`,
    },
    {
      title: "Net Pay",
      dataIndex: "netPay",
      key: "netPay",
      render: (amount: any) => `$${(Number(amount) || 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: PayrollInvoiceStatus) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "view",
                label: "View Details",
                icon: <EyeOutlined />,
                onClick: () => handleViewDetail(record.id),
              },
              ...(record.status === PayrollInvoiceStatus.APPROVED
                ? [
                    {
                      key: "paid",
                      label: "Mark as Paid",
                      icon: <DollarOutlined />,
                      onClick: () => handleMarkAsPaid(record.id),
                    },
                  ]
                : []),
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageMeta title="Payroll | Art Handler" />
      <BillingSubNav />

      {/* Currently Clocked In */}
      <Card
        title={
          <div className="flex items-center space-x-2">
            <TeamOutlined className="text-green-500" />
            <span>Currently Clocked In</span>
            <Badge
              count={clockedInStaff.length}
              style={{ backgroundColor: clockedInStaff.length > 0 ? "#52c41a" : "#d9d9d9" }}
            />
          </div>
        }
        className="shadow-sm mb-4"
        size="small"
      >
        {clockedInStaff.length === 0 ? (
          <div className="text-gray-400 text-center py-2">
            No staff currently clocked in
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {clockedInStaff.map((staff: any) => {
              const clockedSince = staff.lastClockIn
                ? new Date(staff.lastClockIn)
                : null;
              const hoursIn = clockedSince
                ? ((Date.now() - clockedSince.getTime()) / 3600000).toFixed(1)
                : "--";
              const clockedTime = clockedSince
                ? clockedSince.toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "";

              return (
                <Tooltip
                  key={staff.id}
                  title={`Clocked in at ${clockedTime} (${hoursIn}h active)`}
                >
                  <div className="flex items-center space-x-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <Badge dot status="success">
                      <Avatar size="small" icon={<UserOutlined />} />
                    </Badge>
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {staff.user?.firstName} {staff.user?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {hoursIn}h &middot; since {clockedTime}
                      </div>
                    </div>
                    <Button
                      size="small"
                      danger
                      type="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenClockOut(staff);
                      }}
                      style={{ marginLeft: 8, fontSize: 11 }}
                    >
                      Clock Out
                    </Button>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        )}
      </Card>

      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Draft"
              value={dashboardData?.totalDraft || 0}
              prefix={<DollarOutlined />}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Approval"
              value={dashboardData?.totalPending || 0}
              prefix={<ClockCircleOutlined />}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Approved"
              value={dashboardData?.totalApproved || 0}
              prefix={<CheckOutlined />}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Amount"
              value={dashboardData?.totalAmount || 0}
              prefix="$"
              precision={2}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
      </Row>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Payroll Management</h1>
        <Space>
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setDateRange([
                  dates[0]?.format("YYYY-MM-DD") || "",
                  dates[1]?.format("YYYY-MM-DD") || "",
                ]);
              } else {
                setDateRange(null);
              }
            }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreatePayrollInvoice}
          >
            Create Payroll Invoice
          </Button>
        </Space>
      </div>

      <Card title="Payroll Invoices" className="shadow-sm">
        <Table
          dataSource={payrollInvoices?.data || []}
          columns={columns}
          loading={invoicesLoading}
          pagination={{
            current: payrollInvoices?.page || 1,
            pageSize: payrollInvoices?.limit || 50,
            total: payrollInvoices?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} invoices`,
          }}
          locale={{
            emptyText:
              "No payroll invoices found. Create your first payroll invoice to get started.",
          }}
        />
      </Card>

      {/* Time Sheets */}
      <Card
        title={
          <div className="flex items-center space-x-2">
            <ClockCircleOutlined />
            <span>Time Sheets</span>
          </div>
        }
        className="shadow-sm mt-6"
        extra={
          <Space>
            <Select
              placeholder="All Staff"
              allowClear
              style={{ width: 200 }}
              onChange={(val) => { setWsStaffFilter(val); setWsPage(1); }}
              value={wsStaffFilter}
              loading={staffsLoading}
              showSearch
              filterOption={(input, option) =>
                String(option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            >
              {staffsData?.items?.map((s: any) => (
                <Option
                  key={s.id}
                  value={s.id}
                  label={`${s.user?.firstName} ${s.user?.lastName}`}
                >
                  {s.user?.firstName} {s.user?.lastName}
                </Option>
              ))}
            </Select>
            <RangePicker
              onChange={(dates) => {
                if (dates) {
                  setWsDateRange([
                    dates[0]?.format("YYYY-MM-DD") || "",
                    dates[1]?.format("YYYY-MM-DD") || "",
                  ]);
                } else {
                  setWsDateRange(null);
                }
                setWsPage(1);
              }}
            />
          </Space>
        }
      >
        <Table
          dataSource={workSessionsData?.data || []}
          loading={wsLoading}
          rowKey="id"
          pagination={{
            current: workSessionsData?.page || 1,
            pageSize: workSessionsData?.limit || 20,
            total: workSessionsData?.total || 0,
            onChange: (p) => setWsPage(p),
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} sessions`,
          }}
          locale={{ emptyText: "No work sessions found for the selected filters." }}
          columns={[
            {
              title: "Employee",
              key: "employee",
              render: (_: any, record: any) => (
                <div>
                  <div className="font-medium">
                    {record.staff?.user?.firstName} {record.staff?.user?.lastName}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {record.staff?.position || ""}
                  </div>
                </div>
              ),
            },
            {
              title: "Date",
              key: "date",
              render: (_: any, record: any) =>
                record.clockIn
                  ? new Date(record.clockIn).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })
                  : "--",
            },
            {
              title: "Clock In",
              key: "clockIn",
              render: (_: any, record: any) =>
                record.clockIn
                  ? new Date(record.clockIn).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : "--",
            },
            {
              title: "Clock Out",
              key: "clockOut",
              render: (_: any, record: any) =>
                record.clockOut ? (
                  new Date(record.clockOut).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })
                ) : (
                  <Tag color="green">Active</Tag>
                ),
            },
            {
              title: "Break",
              key: "break",
              render: (_: any, record: any) =>
                record.unpaidBreakMinutes > 0
                  ? `${record.unpaidBreakMinutes}m`
                  : "-",
            },
            {
              title: "Duration",
              key: "duration",
              render: (_: any, record: any) => {
                if (record.duration != null) {
                  const h = Math.floor(record.duration);
                  const m = Math.round((record.duration - h) * 60);
                  return (
                    <span className="font-medium">
                      {h > 0 ? `${h}h ` : ""}{m > 0 ? `${m}m` : h > 0 ? "" : "0m"}
                    </span>
                  );
                }
                if (record.clockIn && !record.clockOut) {
                  const hrs = ((Date.now() - new Date(record.clockIn).getTime()) / 3600000).toFixed(1);
                  return <span className="text-green-600 font-medium">{hrs}h (active)</span>;
                }
                return "--";
              },
            },
            {
              title: "Notes",
              dataIndex: "notes",
              key: "notes",
              ellipsis: true,
              width: 150,
              render: (text: string) => text || "-",
            },
            {
              title: "Actions",
              key: "actions",
              width: 80,
              render: (_: any, record: any) => (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "edit",
                        label: "Edit",
                        icon: <EyeOutlined />,
                        onClick: () => {
                          setEditSessionModal(record);
                          editSessionForm.setFieldsValue({
                            clockIn: record.clockIn ? dayjs(record.clockIn) : null,
                            clockOut: record.clockOut ? dayjs(record.clockOut) : null,
                            notes: record.notes,
                            unpaidBreakMinutes: record.unpaidBreakMinutes || 0,
                          });
                        },
                      },
                      {
                        key: "delete",
                        label: "Delete",
                        danger: true,
                        onClick: () => {
                          Modal.confirm({
                            title: "Delete Work Session",
                            content: "Are you sure? This cannot be undone.",
                            okText: "Delete",
                            okType: "danger",
                            onOk: () => deleteSessionMutation.mutate(record.id),
                          });
                        },
                      },
                    ],
                  }}
                >
                  <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
              ),
            },
          ]}
        />
      </Card>

      {/* Edit Work Session Modal */}
      <Modal
        title="Edit Work Session"
        open={!!editSessionModal}
        onCancel={() => setEditSessionModal(null)}
        footer={null}
        width={480}
      >
        <Form
          form={editSessionForm}
          layout="vertical"
          onFinish={(values) => {
            editSessionMutation.mutate({
              id: editSessionModal.id,
              body: {
                clockIn: values.clockIn?.toISOString(),
                clockOut: values.clockOut?.toISOString(),
                notes: values.notes,
                unpaidBreakMinutes: values.unpaidBreakMinutes,
              },
            });
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="clockIn" label="Clock In">
                <DatePicker showTime format="MM/DD/YYYY h:mm A" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="clockOut" label="Clock Out">
                <DatePicker showTime format="MM/DD/YYYY h:mm A" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="unpaidBreakMinutes" label="Unpaid Break">
            <Select>
              {BREAK_OPTIONS.map((mins) => (
                <Option key={mins} value={mins}>
                  {mins === 0 ? "None" : `${mins} minutes`}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Shift notes..." />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={editSessionMutation.isPending}
              >
                Save Changes
              </Button>
              <Button onClick={() => setEditSessionModal(null)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <div>
            <div className="text-lg font-semibold">Create Payroll Invoice</div>
            <div className="text-sm text-gray-500 mt-1">
              Create a new payroll invoice for an employee. The system will
              automatically calculate hours and pay based on work sessions.
            </div>
          </div>
        }
        open={createModalVisible}
        onCancel={handleCreateCancel}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleCreateSubmit} layout="vertical">
          <Form.Item name="period" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="periodType" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="staffId"
            label="Select Employee"
            rules={[{ required: true, message: "Please select an employee" }]}
          >
            <Select
              placeholder="Search and choose an employee"
              loading={staffsLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = String(option?.label || "");
                return optionText.toLowerCase().includes(input.toLowerCase());
              }}
              optionLabelProp="label"
            >
              {staffsData?.items?.map((staff: any) => (
                <Option
                  key={staff.id}
                  value={staff.id}
                  label={`${staff.user?.firstName} ${staff.user?.lastName} - ${staff.user?.email}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {staff.user?.firstName} {staff.user?.lastName}
                    </span>
                    <span className="text-xs text-gray-500">
                      {staff.user?.email}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes (Optional)">
            <TextArea
              rows={3}
              placeholder="Add any additional notes or comments about this payroll invoice..."
            />
          </Form.Item>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <div className="text-sm text-blue-800">
              <strong>Payroll Settings:</strong>
              <div className="mt-2 space-y-1">
                <div>
                  • Pay Period:{" "}
                  <span className="font-medium">
                    {payrollSettings?.payPeriod || "weekly"}
                  </span>
                </div>
                <div>
                  • Period Type:{" "}
                  <span className="font-medium">
                    {payrollSettings?.payPeriod === "biweekly"
                      ? "Bi-weekly"
                      : payrollSettings?.payPeriod === "monthly"
                      ? "Monthly"
                      : "Weekly"}
                  </span>
                </div>
                <div>
                  • Payment Day:{" "}
                  <span className="font-medium">
                    {payrollSettings?.paymentDay || "Friday"}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-sm text-blue-800 mt-3">
              <strong>Note:</strong> When you create this payroll invoice, the
              system will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>
                  Automatically calculate regular and overtime hours from work
                  sessions
                </li>
                <li>Apply the employee's current pay rates</li>
                <li>Include any bonuses or deductions</li>
                <li>Generate the final gross and net pay amounts</li>
                <li>Create and sync with QuickBooks immediately</li>
              </ul>
            </div>
          </div>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  createWithQuickBooksMutation.isPending || settingsLoading
                }
                icon={<PlusOutlined />}
              >
                Create Payroll Invoice
              </Button>
              <Button onClick={handleCreateCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <div className="flex items-center space-x-2">
            <FileTextOutlined className="text-blue-500" />
            <span className="text-lg font-semibold">
              Payroll Invoice Details
            </span>
          </div>
        }
        open={detailModalVisible}
        onCancel={handleDetailModalClose}
        footer={[
          <Button key="close" onClick={handleDetailModalClose}>
            Close
          </Button>,
        ]}
        width={800}
        className="top-8"
      >
        <Spin spinning={detailLoading}>
          {invoiceDetail && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <Title level={4} className="mb-2">
                      Invoice #{invoiceDetail.invoiceNumber}
                    </Title>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>
                        <CalendarOutlined />{" "}
                        {new Date(invoiceDetail.createdAt).toLocaleDateString()}
                      </span>
                      <Tag color={getStatusColor(invoiceDetail.status)}>
                        {invoiceDetail.status.toUpperCase()}
                      </Tag>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">
                      ${(Number(invoiceDetail.netPay) || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500">Net Pay</div>
                  </div>
                </div>
              </div>

              <Card
                title={
                  <span>
                    <UserOutlined /> Employee Information
                  </span>
                }
                size="small"
              >
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Name">
                    {invoiceDetail.staff?.user?.firstName}{" "}
                    {invoiceDetail.staff?.user?.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {invoiceDetail.staff?.user?.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Staff">
                    {formatStaffName(invoiceDetail.staff)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Regular Rate">
                    ${(Number(invoiceDetail.regularRate) || 0).toFixed(2)}/hr
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card
                title={
                  <span>
                    <CalendarOutlined /> Pay Period
                  </span>
                }
                size="small"
              >
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Period Start">
                    {new Date(invoiceDetail.periodStart).toLocaleDateString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period End">
                    {new Date(invoiceDetail.periodEnd).toLocaleDateString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period Type">
                    {invoiceDetail.periodType}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Days">
                    {Math.ceil(
                      (new Date(invoiceDetail.periodEnd).getTime() -
                        new Date(invoiceDetail.periodStart).getTime()) /
                        (1000 * 60 * 60 * 24)
                    )}{" "}
                    days
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card
                title={
                  <span>
                    <ClockCircleOutlined /> Hours & Pay Breakdown
                  </span>
                }
                size="small"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Regular Hours:</span>
                      <span className="font-medium">
                        {(Number(invoiceDetail.regularHours) || 0).toFixed(1)}h
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Overtime Hours:</span>
                      <span className="font-medium">
                        {(Number(invoiceDetail.overtimeHours) || 0).toFixed(1)}h
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Hours:</span>
                      <span className="font-medium">
                        {(
                          (Number(invoiceDetail.regularHours) || 0) +
                          (Number(invoiceDetail.overtimeHours) || 0)
                        ).toFixed(1)}
                        h
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Regular Pay:</span>
                      <span className="font-medium">
                        ${(Number(invoiceDetail.regularPay) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Overtime Pay:</span>
                      <span className="font-medium">
                        ${(Number(invoiceDetail.overtimePay) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Gross Pay:</span>
                      <span className="font-medium text-green-600">
                        ${(Number(invoiceDetail.grossPay) || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              {(Number(invoiceDetail.deductions) || 0) > 0 && (
                <Card
                  title={
                    <span>
                      <DollarOutlined /> Deductions
                    </span>
                  }
                  size="small"
                >
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Deductions:</span>
                      <span className="font-medium text-red-600">
                        -${(Number(invoiceDetail.deductions) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>Net Pay:</span>
                      <span className="text-green-600">
                        ${(Number(invoiceDetail.netPay) || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </Card>
              )}

              {(() => {
                const qbInvoice = invoiceDetail.metadata?.quickbooksInvoice;
                if (!qbInvoice || typeof qbInvoice !== "object") return null;

                const qbData = qbInvoice as Record<string, unknown>;

                return (
                  <Card
                    title={
                      <span>
                        <FileTextOutlined /> QuickBooks Invoice
                      </span>
                    }
                    size="small"
                  >
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="QB Invoice ID">
                        {qbData.qbInvoiceId
                          ? String(qbData.qbInvoiceId)
                          : "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="QB Customer ID">
                        {qbData.qbCustomerId
                          ? String(qbData.qbCustomerId)
                          : "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Sync Status">
                        <Tag color="green">Synced</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Sync Date">
                        {new Date(invoiceDetail.createdAt).toLocaleDateString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                );
              })()}

              {invoiceDetail.workSessions &&
                invoiceDetail.workSessions.length > 0 && (
                  <Card
                    title={
                      <span>
                        <ClockCircleOutlined /> Work Sessions (
                        {invoiceDetail.workSessions.length})
                      </span>
                    }
                    size="small"
                  >
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {invoiceDetail.workSessions.map(
                        (session: any) => {
                          const clockIn = session.clockIn ? new Date(session.clockIn) : null;
                          const clockOut = session.clockOut ? new Date(session.clockOut) : null;
                          const duration = session.duration
                            ? Number(session.duration).toFixed(1)
                            : clockIn && clockOut
                            ? ((clockOut.getTime() - clockIn.getTime()) / 3600000).toFixed(1)
                            : "--";

                          return (
                            <div
                              key={session.id}
                              className="flex justify-between items-center p-2 bg-gray-50 rounded"
                            >
                              <div className="text-sm">
                                <div className="font-medium">
                                  {clockIn
                                    ? clockIn.toLocaleDateString("en-US", {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                      })
                                    : "Unknown date"}
                                </div>
                                <div className="text-gray-500">
                                  {clockIn
                                    ? clockIn.toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                      })
                                    : "--"}
                                  {" - "}
                                  {clockOut
                                    ? clockOut.toLocaleTimeString("en-US", {
                                        hour: "numeric",
                                        minute: "2-digit",
                                        hour12: true,
                                      })
                                    : "active"}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium">
                                  {duration}h
                                </div>
                                {session.unpaidBreakMinutes > 0 && (
                                  <div className="text-xs text-red-500">
                                    -{session.unpaidBreakMinutes}m break
                                  </div>
                                )}
                                {session.notes && (
                                  <Tooltip title={session.notes}>
                                    <div className="text-xs text-gray-400 truncate max-w-[120px]">
                                      {session.notes}
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </Card>
                )}

              {invoiceDetail.notes && (
                <Card title={<span>Notes</span>} size="small">
                  <Text className="text-gray-700">{invoiceDetail.notes}</Text>
                </Card>
              )}
            </div>
          )}
        </Spin>
      </Modal>

      {/* Clock Out Modal */}
      <Modal
        title={
          <div>
            <div className="text-lg font-semibold">
              Clock Out {clockOutStaff?.user?.firstName} {clockOutStaff?.user?.lastName}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              End this staff member's shift and log their time.
            </div>
          </div>
        }
        open={clockOutModalVisible}
        onCancel={() => {
          setClockOutModalVisible(false);
          setClockOutStaff(null);
          clockOutForm.resetFields();
        }}
        footer={null}
        width={480}
      >
        <Form
          form={clockOutForm}
          onFinish={handleClockOutSubmit}
          layout="vertical"
          initialValues={{ unpaidBreakMinutes: 0 }}
        >
          {clockOutStaff?.lastClockIn && (
            <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm">
              <div className="text-gray-600">
                <strong>Clocked in at:</strong>{" "}
                {new Date(clockOutStaff.lastClockIn).toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
              <div className="text-gray-600 mt-1">
                <strong>Active time:</strong>{" "}
                {((Date.now() - new Date(clockOutStaff.lastClockIn).getTime()) / 3600000).toFixed(1)} hours
              </div>
            </div>
          )}

          <Form.Item
            name="unpaidBreakMinutes"
            label="Unpaid Break Time"
            extra="Off-site lunch or personal breaks. On-site lunch is paid."
          >
            <Select>
              {BREAK_OPTIONS.map((mins) => (
                <Option key={mins} value={mins}>
                  {mins === 0 ? "None" : `${mins} minutes`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes (Optional)">
            <TextArea
              rows={3}
              placeholder="Any notes about this shift..."
              maxLength={500}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                danger
                htmlType="submit"
                loading={clockOutMutation.isPending}
                icon={<ClockCircleOutlined />}
              >
                Confirm Clock Out
              </Button>
              <Button
                onClick={() => {
                  setClockOutModalVisible(false);
                  setClockOutStaff(null);
                  clockOutForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScreenPayroll;
